'use client';

import * as React from 'react';

/**
 * Tailwind breakpoint values (matching globals.css)
 * These match Tailwind's default breakpoints
 */
export const BREAKPOINTS = {
    xs: 475,
    sm: 640,
    md: 768,
    lg: 1024,
    desktop: 1024,
    xl: 1280,
    '2xl': 1536,
} as const;

export type Breakpoint = keyof typeof BREAKPOINTS;

/**
 * Hook to check if the viewport is below a given Tailwind breakpoint.
 * Uses max-width media query (matches Tailwind's responsive behavior).
 *
 * @param breakpoint - Tailwind breakpoint name (xs, sm, md, lg, xl, 2xl)
 * @returns true if viewport width is below the breakpoint
 *
 * @example
 * const isMobile = useMediaQuery('md'); // true if width < 768px
 * const isSmall = useMediaQuery('sm'); // true if width < 640px
 */
export function useMediaQueryBelow(breakpoint: Breakpoint) {
    const [matches, setMatches] = React.useState<boolean | undefined>(
        undefined,
    );

    React.useEffect(() => {
        const breakpointValue = BREAKPOINTS[breakpoint];
        const mql = window.matchMedia(`(max-width: ${breakpointValue - 1}px)`);
        const onChange = () => {
            setMatches(window.innerWidth < breakpointValue);
        };
        mql.addEventListener('change', onChange);
        setMatches(window.innerWidth < breakpointValue);
        return () => mql.removeEventListener('change', onChange);
    }, [breakpoint]);

    return !!matches;
}
